import React from "react";

// =====================
// Content Block (Dark Neon)
// =====================
function ContentBlock({ title, description, count, reverse }) {
  const direction = reverse ? "md:flex-row-reverse" : "md:flex-row";

  return (
    <div className="flex w-full items-start">
      {/* Counter */}
      <div className="hidden md:block text-6xl font-extrabold text-white/10 mt-[-20px] mx-4">
        {count}
      </div>

      <div
        className="flex-1 p-6 rounded-2xl relative"
        style={{
          background: "rgba(255,255,255,0.04)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(0,245,160,0.2)",
          boxShadow: "0 0 25px rgba(0,245,160,0.12)",
        }}
      >
        <div className={`flex flex-col ${direction} items-center gap-6`}>
          {/* Visual */}
          <div className="w-full md:w-1/2 h-52 rounded-xl bg-gradient-to-br from-[#00F5A0]/20 to-[#00CFFF]/20 flex items-center justify-center">
            <span className="text-8xl text-[#00F5A0]/40">✦</span>
          </div>

          {/* Text */}
          <div className="flex-1 space-y-3">
            <h3 className="text-3xl font-bold text-[#00F5A0]">{title}</h3>
            <p className="text-sm text-white/70 leading-relaxed max-w-sm">
              {description}
            </p>
            <span className="text-xs font-semibold text-[#00CFFF] hover:underline cursor-pointer">
              Read more →
            </span>
          </div>
        </div>

        {/* Accents */}
        <div className="absolute top-0 right-0 w-3 h-3 bg-[#00F5A0] rounded-sm" />
        <div className="absolute bottom-4 left-4 w-2 h-2 bg-[#00CFFF] rounded-full" />
      </div>
    </div>
  );
}

// =====================
// Main Section
// =====================
export default function WebDevelopmentUI() {
  const gradientText = {
    background: "linear-gradient(90deg, #00F5A0, #00CFFF)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-20 text-white relative bg-[#0B0E11] overflow-hidden">
      {/* Heading – Mobile */}
      <h2
        className="text-4xl font-extrabold mb-10 text-center lg:hidden"
        style={gradientText}
      >
        WEB DEVELOPMENT
      </h2>

      {/* Heading – Desktop Vertical */}
      <h2
        className="hidden lg:block absolute top-2/3 left-0 -translate-y-1/2 text-6xl font-extrabold tracking-wide"
        style={{
          ...gradientText,
          transform: "rotate(-90deg) translateY(50%)",
          transformOrigin: "left center",
        }}
      >
        WEB DEVELOPMENT
      </h2>

      {/* Mockup and Features Container */}
      <div className="relative z-10 max-w-6xl mx-auto flex flex-col space-y-20">
        {/* Browser / Hero Section */}
        <div
          className="rounded-3xl p-8 scale-[0.92] lg:scale-100"
          style={{
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          {/* Browser Bar */}
          <div className="flex justify-between items-center text-white/50 mb-6">
            <div className="flex space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded-full" />
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <div className="w-3 h-3 bg-green-500 rounded-full" />
            </div>
            <span className="text-xs border border-white/20 rounded-full px-3 py-1">
              Search
            </span>
            <span className="text-xs">Menu</span>
          </div>

          {/* Nav */}
          <div className="flex justify-center gap-6 text-sm text-white/70 mb-10">
            {["Home", "About", "Services", "Blog", "Contact"].map((item) => (
              <span key={item} className="hover:text-[#00F5A0] cursor-pointer">
                {item}
              </span>
            ))}
          </div>

          {/* Hero */}
          <div className="flex flex-col md:flex-row gap-10">
            <div className="flex-1 space-y-6">
              <h1 className="text-5xl font-extrabold leading-tight">
                Build <span className="text-[#00F5A0]">Next-Gen</span> Websites
              </h1>
              <button className="px-6 py-3 rounded-full font-semibold bg-gradient-to-r from-[#00F5A0] to-[#00CFFF] hover:opacity-90 transition">
                Get Started →
              </button>
              <p className="text-sm text-white/60">
                High-performance, scalable and conversion-focused web solutions
                tailored for modern businesses.
              </p>
            </div>

            <div className="flex-1 h-64 rounded-2xl bg-gradient-to-br from-[#00F5A0]/20 to-[#00CFFF]/20 flex items-center justify-center">
              <span className="text-9xl text-white/20">⌘</span>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <ContentBlock
            title="Expert Developers"
            description="We build secure, scalable and lightning-fast websites using modern frameworks and best practices."
            count="01"
          />
          <ContentBlock
            title="Modern UX"
            description="Pixel-perfect UI, smooth interactions and responsive layouts that feel premium on every device."
            count="02"
            reverse
          />
        </div>

        {/* Label */}
        <div className="text-center">
          <span className="text-xs uppercase tracking-widest text-white/40 border-b border-white/20 pb-1">
            Website Mockup
          </span>
        </div>
      </div>

      {/* Footer Text */}
      <p className="mt-16 text-center text-white/70 max-w-4xl mx-auto">
        We engineer fast, responsive and conversion-driven websites that scale with
        your business and deliver real results.
      </p>

      {/* CTA */}
      <div className="mt-6 text-center">
        <button className="font-semibold text-[#00F5A0] hover:underline">
          Show More
        </button>
      </div>
    </section>
  );
}
